'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import EMTSyncLogo from '@/components/ui/EMTSyncLogo';

const navItems = [
  { label: 'Dla kogo', href: '#dla-kogo' },
  { label: 'Integracje', href: '#integracje' },
  { label: 'Migracja', href: '#migracja' },
  { label: 'Pakiety', href: '#pakiety' },
  { label: 'Kontakt', href: '#kontakt' },
  { label: 'Śledzenie zamówienia', href: '/track-order' },
];

const toolsItems = [
  { label: 'BaseScan', href: '/narzedzia/basescan', description: 'Sprawdź opłacalność zakupu przed zakupem' },
  // Future items can be added here
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Dynamic logo variant: light for dark background (transparent header on hero), dark for light background (scrolled white header)
  const logoVariant = scrolled ? 'dark' : 'light';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <a href="/" aria-label="EMT Sync — strona główna">
          <EMTSyncLogo
            variant={logoVariant}
            height={52}
            className="w-auto h-[36px] md:h-[52px]"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6" aria-label="Nawigacja główna">
          {navItems?.map((item) => (
            <a
              key={item?.href}
              href={item?.href}
              className={`text-[11px] font-700 uppercase tracking-wide transition-colors ${
                scrolled
                  ? 'text-navy/60 hover:text-navy' :'text-white/60 hover:text-white'
              }`}
            >
              {item?.label}
            </a>
          ))}

          {/* Narzędzia dropdown */}
          <div className="relative" ref={toolsRef}>
            <button
              onClick={() => setToolsOpen(!toolsOpen)}
              className={`flex items-center gap-1 text-[11px] font-700 uppercase tracking-wide transition-colors ${
                scrolled
                  ? 'text-navy/60 hover:text-navy' :'text-white/60 hover:text-white'
              }`}
              aria-expanded={toolsOpen}
              aria-haspopup="true"
            >
              Narzędzia
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {toolsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-border overflow-hidden z-50">
                <div className="py-2">
                  {toolsItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setToolsOpen(false)}
                      className="flex flex-col px-4 py-3 hover:bg-blue-pale transition-colors group"
                    >
                      <span className="text-[11px] font-800 uppercase tracking-wide text-navy group-hover:text-blue transition-colors">
                        {item.label}
                      </span>
                      <span className="text-[10px] text-text-muted mt-0.5 leading-snug">
                        {item.description}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* CTA */}
        <a
          href="#kontakt"
          className="hidden lg:inline-flex btn-primary text-sm py-2.5 px-5"
        >
          Bezpłatna konsultacja
        </a>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Otwórz menu"
        >
          <span className={`block w-6 h-0.5 transition-all ${
            scrolled ? 'bg-navy' : 'bg-white'
          } ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 transition-all ${
            scrolled ? 'bg-navy' : 'bg-white'
          } ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 transition-all ${
            scrolled ? 'bg-navy' : 'bg-white'
          } ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-border px-6 pb-6 pt-4 space-y-4">
          {navItems?.map((item) => (
            <a
              key={item?.href}
              href={item?.href}
              onClick={() => setMenuOpen(false)}
              className="block text-sm font-700 uppercase tracking-widest text-navy/70 hover:text-navy py-2"
            >
              {item?.label}
            </a>
          ))}

          {/* Mobile Narzędzia */}
          <div>
            <button
              onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
              className="flex items-center justify-between w-full text-sm font-700 uppercase tracking-widest text-navy/70 hover:text-navy py-2"
            >
              Narzędzia
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${mobileToolsOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileToolsOpen && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-blue/20">
                {toolsItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => { setMenuOpen(false); setMobileToolsOpen(false); }}
                    className="block text-sm font-600 text-navy/60 hover:text-blue py-2 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a href="#kontakt" onClick={() => setMenuOpen(false)} className="btn-primary w-full justify-center mt-4 text-sm px-4 py-2">
            Bezpłatna konsultacja
          </a>
        </div>
      )}
    </header>
  );
}